import React, { useState } from 'react';
import { BulkCardOperations } from './BulkCardOperations';
import { renderMarkdown } from '../utils/markdown';
import './CardEditor.css';

export const CardEditor = ({
  deckName,
  cards,
  allDecks,
  isCustomDeck,
  onAddCard,
  onUpdateCard,
  onDeleteCard,
  onBulkDelete,
  onBulkTag,
  onBulkMoveToDeck,
  onClose
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  const [arabicText, setArabicText] = useState('');
  const [englishText, setEnglishText] = useState('');
  const [tags, setTags] = useState('');
  const [image, setImage] = useState('');
  const [error, setError] = useState('');

  const resetForm = () => {
    setArabicText('');
    setEnglishText('');
    setTags('');
    setImage('');
    setError('');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size must be less than 2MB');
      return;
    }

    // Convert to data URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
      setError('');
    };
    reader.onerror = () => {
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImage('');
  };

  const handleAdd = () => {
    if (!arabicText.trim() || !englishText.trim()) {
      setError('Both fields are required');
      return;
    }
    const cardTags = tags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    const newCard = {
      ar: arabicText.trim(),
      en: englishText.trim(),
      tags: cardTags
    };
    if (image) {
      newCard.image = image;
    }
    const success = onAddCard(deckName, newCard);
    if (success) {
      resetForm();
      setIsAdding(false);
    } else {
      setError('Failed to add card');
    }
  };

  const handleUpdate = () => {
    if (!arabicText.trim() || !englishText.trim()) {
      setError('Both fields are required');
      return;
    }
    const cardTags = tags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    const updatedCard = {
      ar: arabicText.trim(),
      en: englishText.trim(),
      tags: cardTags
    };
    if (image) {
      updatedCard.image = image;
    }
    const success = onUpdateCard(deckName, editingIndex, updatedCard);
    if (success) {
      resetForm();
      setEditingIndex(null);
    } else {
      setError('Failed to update card');
    }
  };

  const startEdit = (index, card) => {
    setEditingIndex(index);
    setArabicText(card.ar);
    setEnglishText(card.en);
    setTags(card.tags ? card.tags.join(', ') : '');
    setImage(card.image || '');
    setError('');
    setIsAdding(false);
  };

  const handleDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      onDeleteCard(deckName, index);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="card-editor" onClick={(e) => e.stopPropagation()}>
          <div className="card-editor-header">
            <div>
              <h2>{deckName}</h2>
              <p className="deck-subtitle">{cards.length} cards</p>
            </div>
            <div className="card-editor-header-actions">
              {cards.length > 0 && (
                <button
                  className="bulk-ops-btn"
                  onClick={() => setShowBulkOperations(true)}
                >
                  🔧 Bulk Operations
                </button>
              )}
              <button className="close-btn" onClick={onClose}>✕</button>
            </div>
          </div>

        {error && <div className="error-message">{error}</div>}

        <div className="card-editor-content">
          {isCustomDeck && !isAdding && editingIndex === null && (
            <button
              className="btn-add-card"
              onClick={() => {
                setIsAdding(true);
                resetForm();
              }}
            >
              + Add New Card
            </button>
          )}

          {(isAdding || editingIndex !== null) && (
            <div className="card-form">
              <h3>{isAdding ? 'Add New Card' : 'Edit Card'}</h3>
              <div className="markdown-hint">
                💡 Markdown supported: **bold**, *italic*, `code`, lists<br/>
                🔍 Cloze deletions: Use {'{{text}}'} to create fill-in-the-blank cards
              </div>
              <div className="form-group">
                <label>Arabic</label>
                <input
                  type="text"
                  placeholder="Enter Arabic text..."
                  value={arabicText}
                  onChange={(e) => setArabicText(e.target.value)}
                  dir="rtl"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>English</label>
                <input
                  type="text"
                  placeholder="Enter English translation..."
                  value={englishText}
                  onChange={(e) => setEnglishText(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Tags (optional, comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., verbs, daily, beginner"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      isAdding ? handleAdd() : handleUpdate();
                    }
                  }}
                />
              </div>
              <div className="form-group">
                <label>Image (optional)</label>
                <div className="image-upload-container">
                  {image ? (
                    <div className="image-preview-container">
                      <img src={image} alt="Card preview" className="image-preview" />
                      <button
                        type="button"
                        className="btn-remove-image"
                        onClick={removeImage}
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <label className="file-upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="file-input"
                      />
                      <span className="file-upload-text">📷 Upload Image</span>
                    </label>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="btn-save"
                  onClick={isAdding ? handleAdd : handleUpdate}
                >
                  {isAdding ? 'Add Card' : 'Save Changes'}
                </button>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setIsAdding(false);
                    setEditingIndex(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="cards-list">
            {cards.length === 0 ? (
              <div className="empty-state-small">
                {isCustomDeck
                  ? 'No cards yet. Add your first card!'
                  : 'No cards in this deck.'}
              </div>
            ) : (
              cards.map((card, index) => (
                <div
                  key={index}
                  className={`card-item ${editingIndex === index ? 'editing' : ''}`}
                >
                  <div className="card-content">
                    {card.image && (
                      <div className="card-thumbnail-container">
                        <img src={card.image} alt="Card" className="card-thumbnail" />
                      </div>
                    )}
                    <div className="card-text-content">
                      <div
                        className="card-text arabic"
                        dir="rtl"
                        dangerouslySetInnerHTML={renderMarkdown(card.ar)}
                      />
                      <div
                        className="card-text english"
                        dangerouslySetInnerHTML={renderMarkdown(card.en)}
                      />
                      <div className="card-metadata">
                        {card.tags && card.tags.length > 0 && (
                          <div className="card-tags">
                            {card.tags.map((tag, i) => (
                              <span key={i} className="tag">{tag}</span>
                            ))}
                          </div>
                        )}
                        {card.dialect && (
                          <span className="dialect-badge-small">Dialect</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isCustomDeck && (
                    <div className="card-item-actions">
                      <button
                        className="btn-edit"
                        onClick={() => startEdit(index, card)}
                        title="Edit card"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete-card"
                        onClick={() => handleDelete(index)}
                        title="Delete card"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {!isCustomDeck && cards.length > 0 && (
            <div className="info-message">
              This is a default deck. Cards cannot be edited.
            </div>
          )}
        </div>
      </div>
    </div>

    {showBulkOperations && (
      <BulkCardOperations
        deckName={deckName}
        cards={cards}
        allDecks={allDecks}
        isCustomDeck={isCustomDeck}
        onBulkDelete={onBulkDelete}
        onBulkTag={onBulkTag}
        onBulkMoveToDeck={onBulkMoveToDeck}
        onClose={() => setShowBulkOperations(false)}
      />
    )}
  </>
  );
};
